import { useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Table, Typography } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';

import { InvoiceMapping } from '~apis/mapping';
import { usePageHeight } from '~hooks/layout/usePageHeight';
import { useUpdateInvoiceExpectedPaymentDate } from '~hooks/queries/mapping/query';
import { useGetSettings } from '~hooks/queries/setting/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { TaxInvoiceType } from '~models/tax-invoice';

import SelectedSettlementItemFloatBtn from './SelectedSettlementItemFloatBtn';
import SettlementMappingCancelButton from './SettlementMappingCancelButton';
import SettlementWiringButton from './SettlementWiringButton';
import { COLUMNS, headerStyle } from './constant';

export const PAGE_SIZE = 20;

interface Props {
  items: InvoiceMapping[];
  totalElements: number;
  page: number;
  setPage: (page: number) => void;
  size: number;
  setSize: (size: number) => void;
  type: TaxInvoiceType;
  onUpdatePaymentDate: () => void;
  onMappingSuccess: () => void;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  setSort: (sort: string[]) => void;
}

const SettlementListTable: React.FC<Props> = ({
  items,
  totalElements,
  page,
  setPage,
  size,
  setSize,
  type,
  onUpdatePaymentDate,
  onMappingSuccess,
  startDate,
  endDate,
  setSort,
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const [selectedItem, setSelectedItem] = useState<InvoiceMapping[]>([]);

  const height = usePageHeight(180);
  const setting = useGetSettings();

  const wiringEnabled = setting.data?.Data?.data?.features?.wiring;

  const { mutateAsync: updateExpectedPaymentDate } = useUpdateInvoiceExpectedPaymentDate();
  const onUpdateDepositDate = async ({
    depositDate,
    item,
  }: {
    depositDate: string | null;
    item: InvoiceMapping;
  }) => {
    const targetKeyword = type === 'SALES' ? '입금' : '출금';
    const response = await updateExpectedPaymentDate({
      companyId,
      taxInvoiceId: item.taxInvoiceId,
      expectedPaymentDate: depositDate,
      type,
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success(`예정 ${targetKeyword}일이 변경되었습니다.`);
      onUpdatePaymentDate();
    } else {
      Toast.error(`예정 ${targetKeyword}일 변경에 실패했습니다.`);
    }
  };

  const showYear = startDate.year() !== endDate.year();
  const totalUnMappedAmount = selectedItem.reduce((acc, cur) => acc + cur.unmappedAmount, 0);
  const cancelTargetItems = selectedItem
    .filter((item) => item.mappedCount > 0 && item.taxInvoiceId)
    .map((item) => item.taxInvoiceId);

  return (
    <Flex
      style={{
        height,
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <Table
          className={css`
            padding: 0 16px;
            &&& .ant-table-thead > tr > td:first-child {
              border-start-start-radius: 0px;
            }
            &&& .ant-table-thead > tr > td:last-child {
              border-start-end-radius: 0px;
            }
            &&& .ant-table-pagination.ant-pagination {
              padding-top: 8px;
              padding-right: 60px;
            }

            &&& .ant-table-selection-column {
              padding: 4px 8px 4px 12px !important;
            }

            &&& .ant-table-container {
              background-color: white;
            }

            &&& .ant-table-cell {
              border-bottom: ${items.length === 0 ? 0 : ''};
            }
          `}
          scroll={{
            x: '800px',
            y: height,
          }}
          columns={COLUMNS({
            onUpdateDepositDate,
            showYear,
            onMappingSuccess,
            type,
            wiringEnabled,
          }).map((column) => ({
            ...column,
            onHeaderCell: () => ({
              style: headerStyle,
            }),
          }))}
          size="small"
          pagination={{
            onChange: (page) => {
              setPage(page - 1);
            },
            simple: true,
            pageSize: size,
            current: page + 1,
            total: totalElements,
            showSizeChanger: true,
            onShowSizeChange(current, size) {
              setSize(size);
            },
          }}
          onChange={(_, __, sort) => {
            const sortReslut = sort as SorterResult<InvoiceMapping>;
            if (!sortReslut?.field) {
              return;
            }
            if (!sortReslut?.order) {
              setSort([]);
              return;
            }
            setSort([`${sortReslut.field},${sortReslut.order === 'ascend' ? 'asc' : 'desc'}`]);
          }}
          dataSource={items.map((item) => {
            return {
              ...item,
              key: item.taxInvoiceId,
            };
          })}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedItem.map((item) => item.taxInvoiceId),
            onChange(_, selectedRows) {
              const existingItems = selectedItem.filter(
                (item) =>
                  !items.some((currentItem) => currentItem.taxInvoiceId === item.taxInvoiceId),
              );
              setSelectedItem([...existingItems, ...selectedRows]);
            },
          }}
          onRow={() => {
            return {
              style: {
                backgroundColor: 'white',
              },
            };
          }}
        />
        {totalElements ? (
          <Typography.Text
            style={{
              fontSize: 14,
              position: 'absolute',
              bottom: 22,
              left: 40,
            }}
          >{`총 ${totalElements ?? 0}건`}</Typography.Text>
        ) : (
          <div />
        )}
        {selectedItem.length > 0 && (
          <SelectedSettlementItemFloatBtn
            totalLength={selectedItem.length}
            totalAmount={totalUnMappedAmount}
            rightSlot={
              <Flex align="center" gap={24}>
                {type === 'PURCHASE' && (
                  <SettlementWiringButton
                    selectedItem={selectedItem}
                    setSelectedItem={setSelectedItem}
                  />
                )}
                <SettlementMappingCancelButton
                  targetIds={cancelTargetItems}
                  type={type}
                  onMappingSuccess={() => {
                    onMappingSuccess();
                    setSelectedItem([]);
                  }}
                />
              </Flex>
            }
          />
        )}
      </div>
    </Flex>
  );
};

export default SettlementListTable;
